import { useAddressAutocompleteLazyQuery } from '@codezo/graphql';
import { Loader2, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { Input } from './input';

interface AddressAutocompleteProps {
	onSelect: (placeId: string, description: string) => void;
	placeholder?: string;
	/** Restricts suggestions to one country. Null searches everywhere. */
	countryCode?: string | null;
}

export function AddressAutocomplete({
	onSelect,
	placeholder = 'Search for address…',
	countryCode = null,
}: AddressAutocompleteProps) {
	const [query, setQuery] = useState('');
	const [suggestions, setSuggestions] = useState<Array<{ value: string; placeId: string }>>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [sessionToken] = useState(() => crypto.randomUUID());
	const debouncedQuery = useDebouncedValue(query, 300);
	const ref = useRef<HTMLDivElement>(null);

	const [searchAddress] = useAddressAutocompleteLazyQuery();

	useEffect(() => {
		if (debouncedQuery && debouncedQuery.length > 2) {
			setIsSearching(true);
			searchAddress({
				variables: {
					input: debouncedQuery,
					countryCode: countryCode || null,
					sessionToken,
				},
			})
				.then(({ data }) => {
					if (data?.addressAutocomplete) {
						setSuggestions(
							data.addressAutocomplete.map((r) => ({
								value: r.description,
								placeId: r.placeId,
							}))
						);
					}
				})
				.finally(() => setIsSearching(false));
		} else {
			setSuggestions([]);
		}
	}, [debouncedQuery, countryCode, sessionToken, searchAddress]);

	// Click outside to close
	useEffect(() => {
		if (suggestions.length === 0) return;
		const handler = (e: MouseEvent) => {
			if (!ref.current?.contains(e.target as Node)) setSuggestions([]);
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [suggestions]);

	return (
		<div className="relative" ref={ref}>
			<div className="relative">
				<MapPin
					size={14}
					className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-4"
				/>
				<Input
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={placeholder}
					className="pl-8"
				/>
				{isSearching && (
					<Loader2
						size={14}
						className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-text-4"
					/>
				)}
			</div>
			{suggestions.length > 0 && (
				<div className="absolute z-10 mt-1 w-full rounded-lg border border-line-2 bg-popover p-1 shadow-md ring-1 ring-foreground/10">
					{suggestions.map((s) => (
						<button
							key={s.placeId}
							/* Explicit: this lives inside a DialogForm, where a bare
							   button would default to submit and save the form. */
							type="button"
							onClick={() => {
								onSelect(s.placeId, s.value);
								setQuery('');
								setSuggestions([]);
							}}
							className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-2 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<MapPin size={12} className="shrink-0 text-text-4" />
							{s.value}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
